import { AnyFormApi, useStore } from "@tanstack/react-form";
import { RefObject, useEffect, useRef } from "react";

/**
 * Scrolls to the first errored field once a submit attempt has settled with errors present. Every
 * fieldMeta key with a NON-empty `errors` array is resolved by its `name` attribute (array paths like
 * `rows[0].hours` included) inside `containerRef` (default: the document); keys with no element (stale
 * removed-index entries) are dropped and the earliest remaining element in DOM order wins.
 */
export const useScrollToFirstError = (form: AnyFormApi, containerRef?: RefObject<HTMLElement>) => {
  const attempts = useStore(form.store, (state) => state.submissionAttempts);
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const handledAttempts = useRef(attempts);

  useEffect(() => {
    // form.reset() sets the attempt counter back to 0; start counting again from there.
    if (attempts < handledAttempts.current) handledAttempts.current = attempts;
    if (isSubmitting || attempts <= handledAttempts.current) return;
    handledAttempts.current = attempts;

    const root: ParentNode = containerRef?.current ?? document;
    const [first] = Object.entries(form.state.fieldMeta)
      .filter(([, meta]) => meta?.errors?.length)
      .map(([name]) => root.querySelector(`[name="${name}"]`))
      .filter((element): element is Element => element !== null)
      .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
    first?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [attempts, isSubmitting, form, containerRef]);
};
